import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { select } from '@shared/datastore/operators/select';
import { Observable } from 'rxjs/Observable';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  pluck,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { Auth } from '../auth/auth.service';
import { ErrorTracking } from '../tracking';
import { RootModelAction } from './actions';
import { ApiHttp } from './api-http.service';
import {
  arrayIsShallowEqual,
  isDefined,
  objectFilterByKeys,
  Path,
  PathList,
  Query,
  QueryParams,
  RawStoreState,
  Reference,
  ReferenceList,
  referenceToReferenceList,
  RootModelEntity,
  sameElements,
  setDiff,
  stringifyQuery,
  toObservable,
} from './helpers';
import { ListObservable } from './list-observable';
import { ObjectObservable } from './object-observable';
import { RootModelLists, RootModelObjects } from './root.model';
import { WebSocketService } from './websocket';

export interface ListOptions<T extends keyof RootModelLists> {
  query: QueryObservable<RootModelEntity<T>>;
}

type ObservableValues<T> = { [K in keyof T]: T[K] | Observable<T[K]> };

export type QueryObservable<Entity> = ObservableValues<Query<Entity>>;

@Injectable()
export class Datastore {
  constructor(
    private store: Store<RawStoreState>,
    private apiHttp: ApiHttp,
    private auth: Auth,
    private webSocketService: WebSocketService,
    private errorTracking: ErrorTracking,
  ) {}

  list<T extends keyof RootModelLists, T2 extends keyof RootModelLists[T]>(
    key1: T,
    key2: T2 | Observable<T2>,
    options?: ListOptions<T>,
  ): ListObservable<T, ReadonlyArray<RootModelLists[T][T2]>> {
    const optionsQuery = options && options.query ? options.query : {};

    const refStream: Observable<Reference<T>> = Observable.combineLatest(
      [
        toObservable(key1),
        toObservable(key2),
        toObservable(optionsQuery.limit),
        toObservable(optionsQuery.offset),
        toObservable(optionsQuery.params),
      ],
      (
        path1: T,
        path2: T2,
        limit: number,
        offset: number,
        params: QueryParams<RootModelEntity<T>>,
      ) => {
        const path: Path<T> = [path1, path2];
        return { path, query: { limit, offset, params } };
      },
    ).pipe(
      tap(ref => {
        const action: RootModelAction<T> = {
          type: 'REQUEST_DATA',
          payload: {
            type: ref.path[0],
            refList: referenceToReferenceList(ref),
          },
        };
        this.store.dispatch(action);
      }),
      shareReplay(1),
    );

    return new ListObservable(
      refStream,
      this.apiHttp,
      this.auth,
      this.store,
      this.errorTracking,
      refStream.pipe(
        switchMap(({ path: [path1, path2], query }) =>
          this.store.pipe(
            select(path1, path2),
            map(data => {
              const queryString = stringifyQuery(query);
              if (data && data.lists && data.lists[queryString]) {
                return ((data.lists[queryString] || { ids: [] }).ids || [])
                  .slice((query && query.offset) || 0)
                  .map(id => data.entities[id].entity);
              }
              return [];
            }),
            distinctUntilChanged(arrayIsShallowEqual),
          ),
        ),
      ),
    );
  }

  object<
    T extends keyof RootModelObjects,
    T2 extends keyof RootModelObjects[T]
  >(
    key1: T,
    key2: T2 | Observable<T2>,
  ): ObjectObservable<RootModelObjects[T][T2]> {
    const refStream: Observable<Reference<T>> = Observable.combineLatest(
      [toObservable(key1), toObservable(key2)],
      (path1: T, path2: T2) => {
        const path: Path<T> = [path1, path2];
        return { path };
      },
    ).pipe(
      switchMap(
        ({ path }) =>
          this.store.pipe(
            pluck<RawStoreState, RawStoreState[T][string] | undefined>(...path),
            take(1),
          ),
        (ref, data) => ({ ref, data }),
      ),
      tap(({ ref, data }) => {
        // Don't fetch if this was fetched in the last second.
        if (!data || Number(data.timeFetched) + 1000 < Date.now()) {
          const action: RootModelAction<T> = {
            type: 'REQUEST_DATA',
            payload: {
              type: ref.path[0],
              refList: referenceToReferenceList(ref),
            },
          };
          this.store.dispatch(action);
        }
      }),
      map(({ ref }) => ref),
      shareReplay(1),
    );

    const subscriptionStream =
      key1 === 'onlineOffline'
        ? this.webSocketService
            .subscribeOnlineOffline(
              refStream.pipe(map(ref => parseInt(ref.path[1], 10))),
            )
            .pipe(combineLatest(refStream, (ss, ref) => ref))
        : refStream;

    return new ObjectObservable(
      refStream,
      this.apiHttp,
      this.store,
      Observable.merge(refStream, subscriptionStream).pipe(
        switchMap(({ path: [path1, path2] }) =>
          this.store.pipe(
            select(path1, path2),
            filter(isDefined),
            map(obj => obj.entity),
            distinctUntilChanged(),
          ),
        ),
      ),
    );
  }

  /* Returns a list of objects at a location.
   * If an observable is passed in for key2 then only the delta will be
   * refetched from the network.
   */
  objects<
    T extends keyof RootModelObjects,
    P extends keyof RootModelObjects[T]
  >(
    key1: T,
    key2: ReadonlyArray<P> | Observable<ReadonlyArray<P>>,
  ): Observable<ReadonlyArray<RootModelObjects[T][P]>> {
    const args$: Observable<string | ReadonlyArray<string>>[] = [
      key1,
      key2,
    ].map(key => (key instanceof Observable ? key : Observable.of(key)));

    const refStream: Observable<ReferenceList<T>> = Observable.combineLatest(
      args$,
    ).pipe(
      map((pathList: PathList<T>) => ({ refList: { pathList } })),
      startWith({ refList: { pathList: [key1, []] as PathList<T> } }),
      pairwise(), // Use pairwise and only fetch new things
      tap(([{ refList: refList1 }, { refList: refList2 }]) => {
        const newIds = setDiff(refList2.pathList[1], refList1.pathList[1]);
        if (newIds.length) {
          const type = refList2.pathList[0];
          const action: RootModelAction<T> = {
            type: 'REQUEST_DATA',
            payload: { type, refList: { pathList: [type, newIds] } },
          };
          this.store.dispatch(action);
        }
      }),
      map(([{ refList: refList1 }, { refList: refList2 }]) => refList2),
      shareReplay(1),
    );

    return refStream.pipe(
      switchMap(({ pathList }) =>
        this.store.pipe(
          select(pathList[0]),
          filter(isDefined),
          map(rawEntitiesObject =>
            objectFilterByKeys<RawStoreState[T][string]>(
              rawEntitiesObject,
              pathList[1],
            ),
          ),
          map(rawEntitiesObject => Object.values(rawEntitiesObject)),
          map(rawEntities => rawEntities.map(rawEntity => rawEntity.entity)),
          distinctUntilChanged(sameElements),
        ),
      ),
    );
  }
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-webapp-compat/webapp/src/app/@shared/datastore/datastore.ts